/**
 * Performance Monitor Service
 * 
 * Comprehensive performance monitoring system for prompt classification operations.
 * Tracks API latency, processing times, success rates, resource usage, and provides
 * adaptive optimization recommendations.
 * 
 * Features:
 * - Real-time metrics collection and analysis
 * - Adaptive timeout calculation based on historical data
 * - Performance degradation detection and alerting
 * - Resource usage monitoring
 * - SLA compliance tracking
 * - Performance trend analysis
 */

import { CategoryLogger, LogLevel } from './category-loader';

/**
 * Performance metric data point
 */
export interface PerformanceMetric {
  /** Timestamp when the metric was recorded */
  timestamp: number;
  /** Duration in milliseconds */
  duration: number;
  /** Whether the operation was successful */
  success: boolean;
  /** Type of operation measured */
  operation: string;
  /** Additional context data */
  context?: Record<string, any>;
}

/**
 * Aggregated performance statistics
 */
export interface PerformanceStats {
  /** Total number of operations measured */
  totalOperations: number;
  /** Number of successful operations */
  successfulOperations: number;
  /** Number of failed operations */
  failedOperations: number;
  /** Overall success rate (0-1) */
  successRate: number;
  /** Average duration in milliseconds */
  averageDuration: number;
  /** Median duration in milliseconds */
  medianDuration: number;
  /** 95th percentile duration */
  p95Duration: number;
  /** 99th percentile duration */
  p99Duration: number;
  /** Minimum duration recorded */
  minDuration: number;
  /** Maximum duration recorded */
  maxDuration: number;
  /** Operations per second rate */
  operationsPerSecond: number;
  /** Time window for these stats (milliseconds) */
  timeWindowMs: number;
}

/**
 * Resource usage metrics
 */
export interface ResourceMetrics {
  /** Memory usage in MB */
  memoryUsageMB: number;
  /** CPU usage percentage */
  cpuUsagePercent: number;
  /** Active connections count */
  activeConnections: number;
  /** Queue depth for async operations */
  queueDepth: number;
  /** Timestamp when metrics were captured */
  timestamp: number;
}

/**
 * Performance alert configuration
 */
export interface AlertConfig {
  /** Minimum success rate threshold (0-1) */
  minSuccessRate: number;
  /** Maximum average latency threshold (ms) */
  maxAverageLatency: number;
  /** Maximum p95 latency threshold (ms) */
  maxP95Latency: number;
  /** Minimum operations per second threshold */
  minOperationsPerSecond: number;
  /** Time window for alert evaluation (ms) */
  alertWindowMs: number;
  /** Enable/disable alerting */
  enabled: boolean;
}

/**
 * Performance trend data
 */
export interface PerformanceTrend {
  /** Time period identifier */
  period: string;
  /** Start timestamp for the period */
  startTime: number;
  /** End timestamp for the period */
  endTime: number;
  /** Performance statistics for the period */
  stats: PerformanceStats;
  /** Comparison with previous period */
  comparison?: {
    successRateChange: number;
    latencyChange: number;
    throughputChange: number;
  };
}

/**
 * Default alert configuration
 */
const DEFAULT_ALERT_CONFIG: AlertConfig = {
  minSuccessRate: 0.95,
  maxAverageLatency: 1000,
  maxP95Latency: 1500,
  minOperationsPerSecond: 1,
  alertWindowMs: 5 * 60 * 1000, // 5 minutes
  enabled: true
};

/**
 * Performance monitoring service singleton
 */
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private readonly logger: CategoryLogger;
  
  // Metrics storage
  private metrics: PerformanceMetric[] = [];
  private resourceMetrics: ResourceMetrics[] = [];
  
  // Configuration
  private readonly maxMetricsCount = 1000; // Keep last 1000 metrics
  private readonly maxResourceMetricsCount = 100; // Keep last 100 resource snapshots
  private alertConfig: AlertConfig = { ...DEFAULT_ALERT_CONFIG };
  
  // Performance tracking
  private operationStartTimes = new Map<string, number>();
  private lastAlertTime = new Map<string, number>();
  
  // Alert callbacks
  private alertCallbacks: Array<(alert: PerformanceAlert) => void> = [];

  private constructor() {
    this.logger = CategoryLogger.getInstance();
    this.logger.info('Performance Monitor initialized', 'PerformanceMonitor');
    
    // Start periodic resource monitoring
    this.startResourceMonitoring();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start timing an operation
   * @param operationId Unique identifier for the operation
   * @param operation Type of operation being timed
   * @param context Additional context data
   */
  public startTiming(operationId: string, operation: string, context?: Record<string, any>): void {
    const startTime = performance.now();
    this.operationStartTimes.set(operationId, startTime);
    
    this.logger.debug('Started timing operation', 'PerformanceMonitor', {
      operationId,
      operation,
      startTime,
      context
    });
  }

  /**
   * End timing an operation and record the metric
   * @param operationId Unique identifier for the operation
   * @param operation Type of operation being timed
   * @param success Whether the operation was successful
   * @param context Additional context data
   * @returns Duration in milliseconds
   */
  public endTiming(
    operationId: string, 
    operation: string, 
    success: boolean, 
    context?: Record<string, any>
  ): number {
    const endTime = performance.now();
    const startTime = this.operationStartTimes.get(operationId);
    
    if (!startTime) {
      this.logger.warn('No start time found for operation', 'PerformanceMonitor', {
        operationId,
        operation
      });
      return 0;
    }
    
    const duration = endTime - startTime;
    this.operationStartTimes.delete(operationId);
    
    // Record the metric
    this.recordMetric({
      timestamp: Date.now(),
      duration,
      success,
      operation,
      context: { ...context, operationId }
    });
    
    this.logger.debug('Completed timing operation', 'PerformanceMonitor', {
      operationId,
      operation,
      duration,
      success,
      context
    });
    
    return duration;
  }

  /**
   * Record a performance metric
   * @param metric Performance metric to record
   */
  public recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    
    // Trim metrics if we exceed the limit
    if (this.metrics.length > this.maxMetricsCount) {
      this.metrics = this.metrics.slice(-this.maxMetricsCount);
    }
    
    // Check for performance alerts
    this.checkPerformanceAlerts();
    
    this.logger.debug('Recorded performance metric', 'PerformanceMonitor', {
      operation: metric.operation,
      duration: metric.duration,
      success: metric.success,
      totalMetrics: this.metrics.length
    });
  }

  /**
   * Get current performance statistics
   * @param timeWindowMs Time window for statistics (default: last hour)
   * @param operation Filter by operation type (optional)
   * @returns Performance statistics
   */
  public getStats(timeWindowMs = 60 * 60 * 1000, operation?: string): PerformanceStats {
    const cutoffTime = Date.now() - timeWindowMs;
    let relevantMetrics = this.metrics.filter(m => m.timestamp >= cutoffTime);
    
    if (operation) {
      relevantMetrics = relevantMetrics.filter(m => m.operation === operation);
    }
    
    if (relevantMetrics.length === 0) {
      return this.getEmptyStats(timeWindowMs);
    }
    
    const durations = relevantMetrics.map(m => m.duration);
    const successfulOps = relevantMetrics.filter(m => m.success).length;
    const sortedDurations = [...durations].sort((a, b) => a - b);
    
    const stats: PerformanceStats = {
      totalOperations: relevantMetrics.length,
      successfulOperations: successfulOps,
      failedOperations: relevantMetrics.length - successfulOps,
      successRate: successfulOps / relevantMetrics.length,
      averageDuration: durations.reduce((sum, d) => sum + d, 0) / durations.length,
      medianDuration: this.getPercentile(sortedDurations, 0.5),
      p95Duration: this.getPercentile(sortedDurations, 0.95),
      p99Duration: this.getPercentile(sortedDurations, 0.99),
      minDuration: Math.min(...durations),
      maxDuration: Math.max(...durations),
      operationsPerSecond: (relevantMetrics.length / timeWindowMs) * 1000,
      timeWindowMs
    };
    
    this.logger.debug('Generated performance stats', 'PerformanceMonitor', {
      timeWindowMs,
      operation,
      stats: {
        totalOps: stats.totalOperations,
        successRate: stats.successRate,
        avgDuration: stats.averageDuration,
        p95Duration: stats.p95Duration
      }
    });
    
    return stats;
  }

  /**
   * Get adaptive timeout based on historical performance
   * @param operation Operation type (optional)
   * @param percentile Percentile to use for timeout calculation (default: 0.95)
   * @param buffer Safety buffer multiplier (default: 1.2)
   * @returns Recommended timeout in milliseconds
   */
  public getAdaptiveTimeout(operation?: string, percentile = 0.95, buffer = 1.2): number {
    const baseTimeout = 1000; // 1 second base timeout
    const maxTimeout = 5000;   // 5 second max timeout
    
    const stats = this.getStats(10 * 60 * 1000, operation); // Last 10 minutes
    
    if (stats.totalOperations < 10) {
      // Not enough data, use base timeout
      return baseTimeout;
    }
    
    const percentileDuration = percentile === 0.95 ? stats.p95Duration : stats.p99Duration;
    const adaptiveTimeout = Math.min(percentileDuration * buffer, maxTimeout);
    
    this.logger.debug('Calculated adaptive timeout', 'PerformanceMonitor', {
      operation,
      percentile,
      buffer,
      baseTimeout,
      percentileDuration,
      adaptiveTimeout,
      totalOperations: stats.totalOperations
    });
    
    return Math.max(adaptiveTimeout, baseTimeout);
  }

  /**
   * Get performance trends over time
   * @param periods Number of time periods to analyze
   * @param periodDurationMs Duration of each period in milliseconds
   * @returns Array of performance trends
   */
  public getPerformanceTrends(periods = 24, periodDurationMs = 60 * 60 * 1000): PerformanceTrend[] {
    const trends: PerformanceTrend[] = [];
    const now = Date.now();
    
    for (let i = 0; i < periods; i++) {
      const endTime = now - (i * periodDurationMs);
      const startTime = endTime - periodDurationMs;
      
      const periodMetrics = this.metrics.filter(
        m => m.timestamp >= startTime && m.timestamp < endTime
      );
      
      if (periodMetrics.length === 0) continue;
      
      const durations = periodMetrics.map(m => m.duration);
      const successfulOps = periodMetrics.filter(m => m.success).length;
      const sortedDurations = [...durations].sort((a, b) => a - b);
      
      const stats: PerformanceStats = {
        totalOperations: periodMetrics.length,
        successfulOperations: successfulOps,
        failedOperations: periodMetrics.length - successfulOps,
        successRate: successfulOps / periodMetrics.length,
        averageDuration: durations.reduce((sum, d) => sum + d, 0) / durations.length,
        medianDuration: this.getPercentile(sortedDurations, 0.5),
        p95Duration: this.getPercentile(sortedDurations, 0.95),
        p99Duration: this.getPercentile(sortedDurations, 0.99),
        minDuration: Math.min(...durations),
        maxDuration: Math.max(...durations),
        operationsPerSecond: (periodMetrics.length / periodDurationMs) * 1000,
        timeWindowMs: periodDurationMs
      };
      
      const trend: PerformanceTrend = {
        period: `${new Date(startTime).toISOString()}_${new Date(endTime).toISOString()}`,
        startTime,
        endTime,
        stats
      };
      
      // Add comparison with previous period if available
      if (trends.length > 0) {
        const previousStats = trends[trends.length - 1].stats;
        trend.comparison = {
          successRateChange: stats.successRate - previousStats.successRate,
          latencyChange: stats.averageDuration - previousStats.averageDuration,
          throughputChange: stats.operationsPerSecond - previousStats.operationsPerSecond
        };
      }
      
      trends.push(trend);
    }
    
    return trends.reverse(); // Return chronological order
  }

  /**
   * Check for performance alerts and trigger callbacks
   */
  private checkPerformanceAlerts(): void {
    if (!this.alertConfig.enabled) return;
    
    const stats = this.getStats(this.alertConfig.alertWindowMs);
    const now = Date.now();
    
    // Check success rate
    if (stats.successRate < this.alertConfig.minSuccessRate) {
      this.triggerAlert({
        type: 'success_rate',
        severity: 'high',
        message: `Success rate (${(stats.successRate * 100).toFixed(1)}%) below threshold (${(this.alertConfig.minSuccessRate * 100).toFixed(1)}%)`,
        currentValue: stats.successRate,
        threshold: this.alertConfig.minSuccessRate,
        timestamp: now,
        context: { stats }
      });
    }
    
    // Check average latency
    if (stats.averageDuration > this.alertConfig.maxAverageLatency) {
      this.triggerAlert({
        type: 'average_latency',
        severity: 'medium',
        message: `Average latency (${stats.averageDuration.toFixed(0)}ms) above threshold (${this.alertConfig.maxAverageLatency}ms)`,
        currentValue: stats.averageDuration,
        threshold: this.alertConfig.maxAverageLatency,
        timestamp: now,
        context: { stats }
      });
    }
    
    // Check p95 latency
    if (stats.p95Duration > this.alertConfig.maxP95Latency) {
      this.triggerAlert({
        type: 'p95_latency',
        severity: 'medium',
        message: `P95 latency (${stats.p95Duration.toFixed(0)}ms) above threshold (${this.alertConfig.maxP95Latency}ms)`,
        currentValue: stats.p95Duration,
        threshold: this.alertConfig.maxP95Latency,
        timestamp: now,
        context: { stats }
      });
    }
    
    // Check operations per second
    if (stats.operationsPerSecond < this.alertConfig.minOperationsPerSecond) {
      this.triggerAlert({
        type: 'throughput',
        severity: 'low',
        message: `Throughput (${stats.operationsPerSecond.toFixed(2)} ops/sec) below threshold (${this.alertConfig.minOperationsPerSecond} ops/sec)`,
        currentValue: stats.operationsPerSecond,
        threshold: this.alertConfig.minOperationsPerSecond,
        timestamp: now,
        context: { stats }
      });
    }
  }

  /**
   * Trigger a performance alert
   */
  private triggerAlert(alert: PerformanceAlert): void {
    const lastAlert = this.lastAlertTime.get(alert.type);
    const alertCooldown = 5 * 60 * 1000; // 5 minutes
    
    // Rate limit alerts
    if (lastAlert && (alert.timestamp - lastAlert) < alertCooldown) {
      return;
    }
    
    this.lastAlertTime.set(alert.type, alert.timestamp);
    
    this.logger.warn(`Performance alert: ${alert.message}`, 'PerformanceMonitor', {
      alertType: alert.type,
      severity: alert.severity,
      currentValue: alert.currentValue,
      threshold: alert.threshold
    });
    
    // Trigger alert callbacks
    this.alertCallbacks.forEach(callback => {
      try {
        callback(alert);
      } catch (error) {
        this.logger.error('Error in alert callback', 'PerformanceMonitor', { error });
      }
    });
  }

  /**
   * Start periodic resource monitoring
   */
  private startResourceMonitoring(): void {
    const monitorInterval = 30000; // 30 seconds
    
    setInterval(() => {
      this.recordResourceMetrics();
    }, monitorInterval);
  }

  /**
   * Record current resource usage metrics
   */
  private recordResourceMetrics(): void {
    try {
      const metrics: ResourceMetrics = {
        memoryUsageMB: this.getMemoryUsage(),
        cpuUsagePercent: 0, // CPU monitoring would require additional libraries
        activeConnections: this.operationStartTimes.size,
        queueDepth: 0, // This would be updated by queue management systems
        timestamp: Date.now()
      };
      
      this.resourceMetrics.push(metrics);
      
      // Trim resource metrics
      if (this.resourceMetrics.length > this.maxResourceMetricsCount) {
        this.resourceMetrics = this.resourceMetrics.slice(-this.maxResourceMetricsCount);
      }
      
      this.logger.debug('Recorded resource metrics', 'PerformanceMonitor', metrics);
    } catch (error) {
      this.logger.error('Error recording resource metrics', 'PerformanceMonitor', { error });
    }
  }

  /**
   * Get current memory usage in MB
   */
  private getMemoryUsage(): number {
    if (typeof performance !== 'undefined' && 'memory' in performance) {
      // Chrome/Edge specific memory API
      const memory = (performance as any).memory;
      return memory.usedJSHeapSize / (1024 * 1024);
    }
    return 0;
  }

  /**
   * Calculate percentile from sorted array
   */
  private getPercentile(sortedArray: number[], percentile: number): number {
    if (sortedArray.length === 0) return 0;
    
    const index = Math.ceil(sortedArray.length * percentile) - 1;
    return sortedArray[Math.max(0, Math.min(index, sortedArray.length - 1))];
  }

  /**
   * Get empty stats object
   */
  private getEmptyStats(timeWindowMs: number): PerformanceStats {
    return {
      totalOperations: 0,
      successfulOperations: 0,
      failedOperations: 0,
      successRate: 0,
      averageDuration: 0,
      medianDuration: 0,
      p95Duration: 0,
      p99Duration: 0,
      minDuration: 0,
      maxDuration: 0,
      operationsPerSecond: 0,
      timeWindowMs
    };
  }

  // Public API methods

  /**
   * Configure alert thresholds
   */
  public configureAlerts(config: Partial<AlertConfig>): void {
    this.alertConfig = { ...this.alertConfig, ...config };
    this.logger.info('Updated alert configuration', 'PerformanceMonitor', this.alertConfig);
  }

  /**
   * Add alert callback
   */
  public onAlert(callback: (alert: PerformanceAlert) => void): void {
    this.alertCallbacks.push(callback);
  }

  /**
   * Get current resource metrics
   */
  public getResourceMetrics(): ResourceMetrics[] {
    return [...this.resourceMetrics];
  }

  /**
   * Reset all metrics
   */
  public reset(): void {
    this.metrics = [];
    this.resourceMetrics = [];
    this.operationStartTimes.clear();
    this.lastAlertTime.clear();
    this.logger.info('Performance metrics reset', 'PerformanceMonitor');
  }

  /**
   * Export metrics for analysis
   */
  public exportMetrics(): {
    performanceMetrics: PerformanceMetric[];
    resourceMetrics: ResourceMetrics[];
    configuration: AlertConfig;
  } {
    return {
      performanceMetrics: [...this.metrics],
      resourceMetrics: [...this.resourceMetrics],
      configuration: { ...this.alertConfig }
    };
  }
}

/**
 * Performance alert interface
 */
export interface PerformanceAlert {
  /** Type of alert */
  type: 'success_rate' | 'average_latency' | 'p95_latency' | 'throughput' | 'resource_usage';
  /** Alert severity level */
  severity: 'low' | 'medium' | 'high' | 'critical';
  /** Human readable alert message */
  message: string;
  /** Current value that triggered the alert */
  currentValue: number;
  /** Threshold value that was exceeded */
  threshold: number;
  /** Timestamp when alert was triggered */
  timestamp: number;
  /** Additional context data */
  context?: any;
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitor.getInstance(); 